import { FC, useCallback, useEffect, useState } from 'react';

import { AdElementText, PenaltiesFormat } from '@adflow/types';
import { FormControl, FormLabel, Heading, Input, Radio, RadioGroup, Stack } from '@chakra-ui/react';
import { useStore } from '../../../../hooks/useStore';

type Props = {
  selectedElement: AdElementText;
};

const PRESET_SEPARATORS = [':', '-'] as const;
type PresetSeparator = (typeof PRESET_SEPARATORS)[number];
type RadioMode = PresetSeparator | 'custom';

const isPresetSeparator = (value: string | null | undefined): value is PresetSeparator =>
  value != null && (PRESET_SEPARATORS as readonly string[]).includes(value);

export const isMatchStatusPresent = (textElement: AdElementText) => {
  if (!textElement.sources?.length) return false;
  return textElement.sources.some((s) => s.name === 'MATCH_STATUS');
};

const PenaltiesFormatControls: FC<Props> = ({ selectedElement }) => {
  const store = useStore((state) => ({
    displayOptions: state.displayOptions,
    setElementPenaltiesFormat: state.setElementPenaltiesFormat,
  }));

  const separator =
    selectedElement.data.penaltiesFormat?.separator ??
    store.displayOptions.penaltiesFormat?.separator ??
    ':';

  const initialMode: RadioMode = isPresetSeparator(separator) ? separator : 'custom';
  const [mode, setMode] = useState<RadioMode>(initialMode);

  useEffect(() => {
    setMode(isPresetSeparator(separator) ? separator : 'custom');
  }, [selectedElement.id, separator]);

  const writeSeparator = useCallback(
    (value: string, mergeKey?: string) => {
      const next: PenaltiesFormat = { separator: value.slice(0, 2) };
      store.setElementPenaltiesFormat(selectedElement.id, next, { mergeKey });
    },
    [store, selectedElement.id],
  );

  const handleModeChange = useCallback(
    (value: string) => {
      if (value === ':' || value === '-') {
        setMode(value);
        writeSeparator(value);
        return;
      }
      if (value === 'custom') {
        setMode('custom');
        // Preserve any existing custom value; if currently on a preset, seed with that value.
        writeSeparator(separator);
      }
    },
    [writeSeparator, separator],
  );

  const handleCustomChange: React.ChangeEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      writeSeparator(event.target.value.slice(0, 2), `penalties-separator:${selectedElement.id}`);
    },
    [writeSeparator, selectedElement.id],
  );

  const customValue = mode === 'custom' ? separator.slice(0, 2) : '';

  return (
    <FormControl alignItems="center" mt={5} mb={2} mr={0}>
      <Heading fontSize="md" fontWeight="semibold" mb={3} noOfLines={1}>
        Penalty score format
      </Heading>
      <Stack direction="row" mb={2} alignItems="center">
        <FormLabel htmlFor="penalties-separator" mb={'0'} style={{ fontVariant: 'all-small-caps' }}>
          Separator
        </FormLabel>
        <RadioGroup
          id="penalties-separator"
          value={mode}
          ml={'auto'}
          mr={0}
          onChange={handleModeChange}
        >
          <Stack direction="row">
            <Radio value=":">4:2</Radio>
            <Radio value="-">4-2</Radio>
            <Radio value="custom">Custom</Radio>
          </Stack>
        </RadioGroup>
      </Stack>
      {mode === 'custom' && (
        <Input
          aria-label="Custom penalty score separator"
          placeholder="e.g. vs"
          maxLength={2}
          value={customValue}
          onChange={handleCustomChange}
          size="sm"
        />
      )}
    </FormControl>
  );
};

PenaltiesFormatControls.displayName = 'PenaltiesFormatControls';

export default PenaltiesFormatControls;
